import {
  Component,
  Input,
  SimpleChanges,
  ViewChild,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NotesComponent } from '../notes/notes.component';
import { GlucoseValueComponent } from './glucose-value/glucose-value.component';
import { MemberDashboardService } from '../member-dashboard.service';
import { ActivatedRoute } from '@angular/router';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { formatDate } from '@angular/common';
// import moment from 'moment';
import moment from 'moment-timezone';
//
import { NgIf, NgFor, NgClass } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
declare var $: any;

@Component({
  selector: 'app-glucose',
  standalone: true,
  imports: [
    MatCheckboxModule,
    NotesComponent,
    GlucoseValueComponent,
    HighchartsChartModule,
    NgIf,
    NgFor,
    NgClass,
    FormsModule,
  ],
  templateUrl: './glucose.component.html',
  styleUrl: './glucose.component.css',
})
export class GlucoseComponent implements OnChanges, OnDestroy {
  @Input() glucoseDetails: any;
  @Input() memberbasic: any;
  @Input() bmiData: any;
  @Input() healthMetricsData: any;
  // Glucose date picker range (yyyy-MM-dd), used by the chatbot's Generate Message button
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @ViewChild('chart') chart: any;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  private chartInstance: any;
  showChart: boolean = true;
  noDataFound: boolean = false;
  emptyMessage: string = '';
  postMeal: any = [];
  cgm: any = [];
  timeLines: any = [];
  fasting: any = [];
  timeZone: any;
  showModal: boolean = false;
  modalType: any;
  modalSTime: any;
  modalETime: any;
  modalDuration: any;
  modalImg: any;
  activityName: any;
  calsBurned: any;
  distCovered: any;
  distUnits: any = 'Km';
  foodName: any;
  foodCals: any;
  foodCarbs: any;
  foodProteins: any;
  foodFats: any;
  medicName: any;
  medicDosage: any;
  medicQuantity: any;
  medicFrequency: any;
  medicStartDate: any;
  medicinesRange: any = [];
  medicinesByTime: any = {};
  memberId: any = this.route.snapshot.queryParamMap.get('memberId');

  // Pristine copy of the glucose API response, captured before it is mutated for charting
  // (timeline entries get merged into `readings`) - used as-is for the chatbot payload.
  rawGlucoseDetails: any = null;
  // True when glucoseDetails has been refreshed (e.g. date range changed) since the last
  // chat message was sent, so the next message re-sends glucose_data even mid-conversation.
  glucoseDataDirty: boolean = false;

  // Glucose Co-pilot chatbot
  showHideChatBot: boolean = false;
  userMessage: string = '';
  messages: { sender: string; text: string; id: number }[] = [];
  conversationId: any = null;
  isSending: boolean = false;
  // True once the opening handshake (startConversation) has returned a conversation_id
  // and the welcome message has been shown - the user can't type before that.
  chatReady: boolean = false;
  // Most recent response_message from the GlycoBrain chat API.
  lastResponseMessage: string = '';
  isGeneratingMessage: boolean = false;
  // Insights parsed out of the "summary" response - the user ticks which ones the
  // generated message should be built from.
  summaryInsights: { number: string; section: string; text: string; selected: boolean }[] = [];
  showInsightPicker: boolean = false;
  // Member archetype the generated message should be tailored to (single-select dropdown).
  archetypes: { name: string; selected: boolean }[] = [
    'Burnt out',
    'Busy professional',
    'Overwhelmed parent',
    'Last-minute canceller',
    "The 'yes' on the call",
    'Motivated',
    'Inconsistent logger',
    'Life transition',
    'Traveling',
    'Willing, needs momentum',
  ].map((name) => ({ name, selected: false }));
  showArchetypeDropdown: boolean = false;
  // "Message to send member through Rally" box under the chat input - holds the
  // generate-message response (or error) and can be edited before sending.
  showRallyMessageBox: boolean = false;
  rallyMessage: string = '';
  isSendingRallyMessage: boolean = false;
  rallySendStatus: { success: boolean; text: string } | null = null;
  // True for 20s after a successful send - the box shows the delivered confirmation.
  rallyDelivered: boolean = false;
  private rallyDeliveredTimer: any;

  // Generate Insight card (/cgm/copilot/analyze)
  isGeneratingInsight: boolean = false;
  insightGenerated: boolean = false;
  insightText: string = '';

  constructor(
    private dashboard: MemberDashboardService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {}

  private buildMedicationTimestamp(
    date: string,
    time: string,
    timeZone: string
  ): number | null {
    if (!date || !time) return null;

    const cleanDate = date.split('T')[0]; // SAFE

    const m = moment.tz(
      `${cleanDate} ${time}`,
      [
        'YYYY-MM-DD h:mm A',
        'YYYY-MM-DD hh:mm A',
        'YYYY-MM-DD H:mm',
        'YYYY-MM-DD HH:mm',
      ],
      timeZone
    );

    return m.isValid() ? m.valueOf() : null;
  }

  ngOnChanges(changes: SimpleChanges) {
    // Keep a pristine copy of the raw API response before it gets mutated below
    // (timeline entries get merged into `readings` for charting) - used by the chatbot payload.
    this.rawGlucoseDetails = this.glucoseDetails
      ? JSON.parse(JSON.stringify(this.glucoseDetails))
      : null;

    // glucoseDetails is reassigned to a new object whenever the member-dashboard's date range
    // picker triggers a fresh fetch. Skip the very first assignment (initial tab load, before
    // any conversation exists) and flag every one after that, so the next chat message - even
    // mid-conversation - re-sends the updated glucose_data for the new range.
    if (changes['glucoseDetails'] && !changes['glucoseDetails'].firstChange) {
      this.glucoseDataDirty = true;
    }

    // Insights belong to the date range they were generated for - a new range resets the
    // Generate Insight button to its empty state.
    if (changes['glucoseDetails']) {
      this.insightGenerated = false;
      this.insightText = '';
    }

    // Reset zoom when date range changes (new data loaded)
    if (changes['glucoseDetails']) {
      // Force chart recreation by updating the options
      this.forceChartRecreation();

      // Reset zoom after chart is recreated with new data
      setTimeout(() => {
        this.resetChartZoom();
      }, 500);
    }

    // Reset data arrays and flags
    this.fasting = [];
    this.postMeal = [];
    this.cgm = [];
    this.timeLines = [];
    this.medicinesByTime = {};
    this.noDataFound = false;
    this.emptyMessage = '';

    if (this.glucoseDetails && this.glucoseDetails.medicinesRange) {
        this.medicinesRange = this.glucoseDetails.medicinesRange;
        this.medicinesByTime = this.glucoseDetails.medicinesByTime || {};
    } else {
        // Ensure it's empty if not provided
        this.medicinesRange = []; 
        this.medicinesByTime = {};
    }

    let maxValue = 0;
    let gRanges = [50, 70, 140, 180];


    // Ensure readings array exists if we have medication data
    if (!this.glucoseDetails.readings && this.medicinesRange && this.medicinesRange.days && 
        this.medicinesRange.days.some((day: any) => day.medicines && day.medicines.length > 0)) {
      this.glucoseDetails.readings = [];
    }
    
    // Check if we have actual glucose readings or medication data
    if (this.glucoseDetails.readings?.length > 0 || (this.medicinesRange && this.medicinesRange.days && 
        this.medicinesRange.days.some((day: any) => day.medicines && day.medicines.length > 0))) {
      this.timeZone = this.glucoseDetails.timeZone;
      
      // Set a default lastReading value if we only have medication data and no glucose readings
      let lastReading = 0;
      const hasOnlyMedicationData = this.fasting.length === 0 && this.postMeal.length === 0 && this.cgm.length === 0 &&
                                  this.medicinesRange && this.medicinesRange.days && 
                                  this.medicinesRange.days.some((day: any) => day.medicines && day.medicines.length > 0);
      
      if (hasOnlyMedicationData) {
        lastReading = 100; 
      }
      
      let lastTimeLines = [];

      // First, merge timeline data into readings array
      for (let i = 0; i < this.glucoseDetails.timeLine?.length; i++) {
        let reading = {
          timeStampKeyVal: this.glucoseDetails.timeLine[i].date,
          reading: 'timeLine',
          platform: 'NA',
          type: this.glucoseDetails.timeLine[i].type,
          data: this.glucoseDetails.timeLine[i].data,
        };

        this.glucoseDetails.readings.push(reading);
      }

      this.glucoseDetails.readings = this.glucoseDetails.readings.sort(
        (a: any, b: any) => {
          return (
            new Date(a.timeStampKeyVal).getTime() -
            new Date(b.timeStampKeyVal).getTime()
          );
        }
      );
      
      let tempMedTimeLines: any[] = [];

      if (this.medicinesRange && this.medicinesRange.days) {
        for (const day of this.medicinesRange.days) {
          if (day.medicines) {
            for (const medicine of day.medicines) {
              // Only show medications with status "Taken" (case-insensitive)
              const medicineStatus =
                medicine.status && medicine.status.status
                  ? medicine.status.status.toLowerCase()
                  : '';
              if (medicineStatus !== 'taken') {
                continue; // Skip medications that are not taken
              }

                const datePart = day.date.substring(0, 10);
                // Use takenTime if available and not empty, otherwise use time
                const timeToUse = medicine.status && 
                                 medicine.status.takenTime && 
                                 medicine.status.takenTime.trim() !== '' 
                                 ? medicine.status.takenTime 
                                 : medicine.time;
                
                const dateStamp = this.buildMedicationTimestamp(
                  day.date,
                  timeToUse,
                  this.timeZone
                );

                  // Add medicine as a 'timeLine' reading for unified processing
                  let reading = {
                    timeStampKeyVal: dateStamp,
                    reading: 'timeLine',
                    platform: 'NA',
                    type: 'Medication', // New type for your timeline logic
                    data: {
                      name: medicine.name,
                      dosage: medicine.dosage,
                      unit: medicine.unit,
                      quantity: medicine.quantity,
                      frequency: medicine.frequency,
                      startDate: medicine.startDate,
                      // Use takenTime if available, otherwise use regular time
                      time: timeToUse,
                      originalTime: medicine.time,
                      takenTime: medicine.status && medicine.status.takenTime ? medicine.status.takenTime : null,
                      status: medicine.status && medicine.status.status ? medicine.status.status : null,
                      date: day.date,
                    },
                  };
                  this.glucoseDetails.readings.push(reading);
              }
            }
          }
          // Re-sort to include the new medicine timeline points
          this.glucoseDetails.readings = this.glucoseDetails.readings.sort(
            (a: any, b: any) => {
              return (
                new Date(a.timeStampKeyVal).getTime() -
                new Date(b.timeStampKeyVal).getTime()
              );
            }
          );
      }
      
      for (let i = 0; i < this.glucoseDetails.readings.length; i++) {
        if (this.glucoseDetails.readings[i].sugarLevel > maxValue) {
          maxValue = this.glucoseDetails.readings[i].sugarLevel;
        }

        var currentDate = formatDate(
          this.glucoseDetails.readings[i].timeStampKeyVal,
          'yyyy-MM-dd HH:mm:ss',
          'en'
        );
        var dateStamp = Date.parse(currentDate);
        if (
          this.glucoseDetails.readings[i].reading.toUpperCase() == 'FASTING' &&
          (this.glucoseDetails.readings[i].platform.toUpperCase() == 'IOS' ||
            this.glucoseDetails.readings[i].platform.toUpperCase() == 'ANDROID')
        ) {
          this.fasting.push([
            dateStamp,
            this.glucoseDetails.readings[i].sugarLevel,
          ]);
          lastReading = this.glucoseDetails.readings[i].sugarLevel;
        } else if (
          this.glucoseDetails.readings[i].reading.toUpperCase() != 'FASTING' &&
          this.glucoseDetails.readings[i].platform.toUpperCase() == 'CGM'
        ) {
          this.cgm.push([
            dateStamp,
            this.glucoseDetails.readings[i].sugarLevel,
          ]);
          lastReading = this.glucoseDetails.readings[i].sugarLevel;
        } else if (
          this.glucoseDetails.readings[i].reading.toUpperCase() == 'TIMELINE'
        ) {
          if (lastReading > 0) {
            let img = '/assets/img/nutritionn.png';
            if (this.glucoseDetails.readings[i].type == 'Sleep') {
              img = '/assets/img/sleepn.png';
            }
            if (this.glucoseDetails.readings[i].type == 'Activity') {
              img = '/assets/img/activityn.png';
            }
            if (this.glucoseDetails.readings[i].type == 'Medication') {
              img = '/assets/img/medicine.png';
            }
            var r = {
              x: dateStamp,
              y: lastReading - 1,
              type: this.glucoseDetails.readings[i].type,
              data: this.glucoseDetails.readings[i].data,
              events: {
                click: (e: any) => {
                  this.showInfo(e.point.type, e.point.data);
                },
              },
              marker: {
                height: 25,
                width: 25,
                symbol: 'url(' + img + ')',
              },
            };
            this.timeLines.push(r);
          } else {
            lastTimeLines.push(this.glucoseDetails.readings[i]);
          }
        } else {
          this.postMeal.push([
            dateStamp,
            this.glucoseDetails.readings[i].sugarLevel,
          ]);
          lastReading = this.glucoseDetails.readings[i].sugarLevel;
        }
        if (lastTimeLines.length > 0 && lastReading > 0) {
          while (lastTimeLines.length > 0) {
            var timeLine = lastTimeLines.pop();

            let img = '/assets/img/nutritionn.png';
            if (timeLine.type == 'Sleep') {
              img = '/assets/img/sleepn.png';
            }
            if (timeLine.type == 'Activity') {
              img = '/assets/img/activityn.png';
            }
            var currentDate1 = formatDate(
              timeLine.timeStampKeyVal,
              'yyyy-MM-dd HH:mm:ss',
              'en'
            );
            var dateStamp1 = Date.parse(currentDate1);

            var r1 = {
              x: dateStamp1,
              y: lastReading - 1,
              type: timeLine.type,
              data: timeLine.data,
              events: {
                click: (e: any) => {
                  this.showInfo(e.point.type, e.point.data);
                },
              },
              marker: {
                height: 25,
                width: 25,
                symbol: 'url(' + img + ')',
              },
            };
            this.timeLines.push(r1);
          }
        }
      }
      if (maxValue >= 180 && maxValue <= 210) {
        gRanges = [50, 70, 140, 180, maxValue + 15];
      } else if (maxValue >= 210 && maxValue <= 240) {
        gRanges = [50, 70, 140, 180, 210, maxValue + 15];
      } else if (maxValue >= 240 && maxValue <= 270) {
        gRanges = [50, 70, 140, 180, 210, 240, maxValue + 15];
      } else if (maxValue > 270) {
        const paddedMax = Math.ceil((maxValue + 15) / 10) * 10;
        gRanges = [50, 70, 140, 180, 210, 240, 270, paddedMax];
      }

      // After processing, check if we actually have any glucose data or medication data
      const hasMedicationData = this.timeLines.some((item: any) => item.type === 'Medication');
      
      if (
        this.fasting.length === 0 &&
        this.postMeal.length === 0 &&
        this.cgm.length === 0 &&
        !hasMedicationData
      ) {
        // We have no glucose readings and no medication data
        this.noDataFound = true;
        this.emptyMessage = 'No Record found for selected date range';
      }
    } else {
      // No glucose readings data
      this.noDataFound = true;
      this.emptyMessage = 'No Record found for selected date range';
    }
    this.createChart(
      this.fasting,
      this.postMeal,
      this.cgm,
      this.timeLines,
      this.timeZone,
      gRanges
    );
  }
  showInfo(type: any, data: any) {
    this.showModal = true;
    var e = document.getElementById('exampleModalCenter');
    if (type == 'Activity') {
      this.modalImg = '/assets/img/activityn.png';
      this.modalType = type;
      this.modalSTime = formatDate(data.startTime, 'hh:mm a', 'en');
      this.activityName = data.type;
      this.calsBurned = data.calsBurned;
      this.distCovered = (data.distanceCovered * 1.609).toFixed(1);
      if (
        this.memberbasic?.address?.country?.toUpperCase() === 'UNITED STATES' ||
        this.memberbasic?.address?.country?.toUpperCase() === 'CANADA'
      ) {
        this.distUnits = 'miles';
        this.distCovered = data.distanceCovered;
      }
    } else if (type == 'Sleep') {
      this.modalImg = '/assets/img/sleepn.png';
      this.modalType = type;
      this.modalSTime = formatDate(data.startTime, 'hh:mm a', 'en');
      this.modalETime = formatDate(data.endTime, 'hh:mm a', 'en');
      const hours = Math.floor(data.duration / 3600);
      const minutes = Math.floor((data.duration % 3600) / 60);
      this.modalDuration = hours + ' hrs ' + minutes + ' min';
    } else if (type == 'Medication') {
      // More detailed debug logging
  
      
      this.modalImg = '/assets/img/medicine.png';
      this.modalType = type;
      this.modalSTime = data.time;
      this.medicName = data.name; 
      this.medicDosage = `${data.dosage} ${data.unit}`; 
      this.medicQuantity = data.quantity !== undefined ? data.quantity : 'N/A'; 
      this.medicFrequency = data.frequency !== undefined ? data.frequency : 'N/A';
    }
     else {
      this.modalImg = '/assets/img/nutritionn.png';
      this.modalType = 'Nutrition';
      this.modalSTime = formatDate(data.time, 'hh:mm a', 'en');
      this.foodName = data.name;
      this.foodCals = data.calories;
      this.foodCarbs = data.carbs;
      this.foodProteins = data.protein;
      this.foodFats = data.fats;
    }
    $('#exampleModalCenter').modal('show');
  }
  closeModal() {
    $('#exampleModalCenter').modal('hide');
  }

  createChart(
    fasting: any,
    postMeal: any,
    cgm: any,
    timeLine: any,
    timeZone: any,
    list: any
  ) {
    const tickValues: number[] = Array.isArray(list) ? list : [];
    const upperBandLimit =
      tickValues.length > 0
        ? Math.max(350, tickValues[tickValues.length - 1])
        : 350;
    const timeLinePoints = Array.isArray(timeLine) ? timeLine : [];
    const medicationPoints: any[] = [];
    const foodLogPoints: any[] = [];
    const workoutPoints: any[] = [];
    const sleepPoints: any[] = [];
    const otherTimelinePoints: any[] = [];

    // Normalize types to be resilient to spacing/casing differences from API
    timeLinePoints.forEach((point: any) => {
      const normalizedType = (point?.type || '')
        .toString()
        .trim()
        .toLowerCase();
      switch (normalizedType) {
        case 'medication':
          medicationPoints.push(point);
          break;
        case 'nutrition':
        case 'food log':
        case 'foodlog':
          foodLogPoints.push(point);
          break;
        case 'activity':
        case 'workout':
          workoutPoints.push(point);
          break;
        case 'sleep':
          sleepPoints.push(point);
          break;
        default:
          // If it looks like a food log but type is missing/mismatched, still bucket it
          const looksLikeFoodLog =
            point?.data &&
            (point.data.calories !== undefined ||
              point.data.carbs !== undefined ||
              point.data.protein !== undefined ||
              point.data.fats !== undefined ||
              point.data.name);
          if (looksLikeFoodLog) {
            foodLogPoints.push(point);
          } else {
            otherTimelinePoints.push(point);
          }
          break;
      }
    });

    this.chartOptions = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x',
        },
        zooming: {
          type: 'x',
        },
      },
      title: { text: 'Glucose Readings' },
      xAxis: [
        {
          crosshair: true,
          lineWidth: 2,
          type: 'datetime',
          dateTimeLabelFormats: {
            millisecond: '%H:%M:%S.%L',
            second: '%H:%M:%S',
            minute: '%H:%M',
            hour: '%H:%M',
            day: '%a,<br/>%b %e',
            week: '%a,<br/>%b %e',
            month: "%b '%y",
            year: '%Y',
          },
        },
      ],
      yAxis: {
        lineWidth: 2,
        title: {
          text: 'Values (mg/dl)',
        },
        tickPositioner: function () {
          return list;
        },
        plotBands: [
          {
            from: 50,
            to: 70,
            color: 'rgba(178, 34, 34, 0.6)', // Dark green
          },
          {
            from: 70,
            to: 140,
            color: 'rgba(0, 128, 0, 0.5)', // Forest green
          },
          {
            from: 140,
            to: 180,
            color: 'rgba(184, 134, 11, 0.5)', // Dark goldenrod
          },
          {
            from: 180,
            to: 210,
            color: 'rgba(205, 92, 92, 0.5)', // Indian red
          },
          {
            from: 210,
            to: 240,
            color: 'rgba(255, 69, 0, 0.5)', // Red-orange
          },
          {
            from: 240,
            to: 270,
            color: 'rgba(178, 34, 34, 0.6)', // Firebrick red
          },
          {
            from: 270,
            to: upperBandLimit,
            color: 'rgba(128, 0, 0, 0.6)', // Maroon
          },
        ],
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
      },
      tooltip: {
        formatter: function () {
          // Disable tooltip for scatter series (timeline icons)
          if (this.series.type === 'scatter') {
            return false;
          }
          if (this.x !== undefined) {
            if (this.series.name == '') {
              return false;
            }
            let conD = new Date(this.x).toLocaleString('en-US', {
              timeZone: timeZone,
            });
            let d = moment(conD).format('ddd, MMM DD');
            let t = moment(conD).format('HH:mm:ss');
            return d + ' at ' + t + '</b> <br> <b>' + this.y + ' mg/dl</b>';
          }
          return 'Sugar Level: ' + this.y + ' mg/dl';
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif',
        },
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false,
          },
          enableMouseTracking: true,
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            fillColor: 'transparent',
          },
        },
      },
      series: [
        {
          name: 'Fasting',
          data: fasting,
          type: 'line',
          color: '#E58009',
        },
        {
          name: 'Post Meal',
          data: postMeal,
          type: 'line',
          color: '#089bab',
        },
        {
          name: 'CGM',
          data: cgm,
          type: 'line',
          color: '#212529',
        },
        {
          name: 'Medication',
          type: 'scatter',
          data: medicationPoints,
          showInLegend: true,
          visible: false,
          marker: {
            symbol: 'url(/assets/img/medicine.png)',
            width: 18,
            height: 18,
          },
        },
        {
          name: 'Food log',
          type: 'scatter',
          data: foodLogPoints,
          showInLegend: true,
          visible: false,
          marker: {
            symbol: 'url(/assets/img/nutritionn.png)',
            width: 18,
            height: 18,
          },
        },
        {
          name: 'Workout',
          type: 'scatter',
          data: workoutPoints,
          showInLegend: true,
          visible: false,
          marker: {
            symbol: 'url(/assets/img/activityn.png)',
            width: 18,
            height: 18,
          },
        },
        {
          name: 'Sleep',
          type: 'scatter',
          data: sleepPoints,
          showInLegend: true,
          visible: false,
          marker: {
            symbol: 'url(/assets/img/sleepn.png)',
            width: 18,
            height: 18,
          },
        },
        {
          name: '',
          type: 'scatter',
          data: otherTimelinePoints,
          showInLegend: false,
        },
      ],
    };

    this.chartOptions.time = {
      timezone: timeZone,
    };

    // Add chart callback to capture instance for zoom control
    this.chartOptions.chart!.events = {
      load: this.chartCallback,
    };
  }

  private chartCallback = (chart: any) => {
    // The chart parameter is actually an event object, get the actual chart from target
    const actualChart = chart.target;
    this.chartInstance = actualChart;
  };

  private resetChartZoom(): void {
    const chart = this.getChartInstance();
    if (
      chart &&
      chart.xAxis &&
      chart.xAxis[0] &&
      typeof chart.xAxis[0].setExtremes === 'function'
    ) {
      chart.xAxis[0].setExtremes(null, null);
    }
  }

  // Force chart recreation when data changes
  private forceChartRecreation(): void {
    // Hide and show chart to force recreation
    this.showChart = false;
    setTimeout(() => {
      this.showChart = true;
    }, 10);
  }

  // Get chart instance from ViewChild if not available
  private getChartInstance(): any {
    if (this.chartInstance) {
      return this.chartInstance;
    }

    // Try to get from ViewChild
    if (this.chart && this.chart.chart) {
      this.chartInstance = this.chart.chart;
      return this.chartInstance;
    }

    return null;
  }

  // rawGlucoseDetails carries UTC instants throughout (readings[].timestamp/timeStampKey/
  // timeStampKeyVal, timeLine[].date). The chatbot needs to reason in the member's own local
  // time, so before sending glucose_data to the API these are converted from UTC into the
  // member's timeZone - as local wall-clock values with no UTC offset, so the API can read
  // them at face value. Note timeLine[].data.time/startTime/endTime are left untouched - the
  // API already returns those as local wall-clock strings with no UTC 'Z' suffix.
  private buildGlucoseDataForChat(): any {
    if (!this.rawGlucoseDetails) return null;

    const timeZone = this.rawGlucoseDetails.timeZone;
    if (!timeZone) {
      return this.rawGlucoseDetails;
    }

    const readings = Array.isArray(this.rawGlucoseDetails.readings)
      ? this.rawGlucoseDetails.readings.map((reading: any) => ({
          ...reading,
          ...this.convertReadingTimestampsToMemberTimeZone(reading, timeZone),
        }))
      : this.rawGlucoseDetails.readings;

    const timeLine = Array.isArray(this.rawGlucoseDetails.timeLine)
      ? this.rawGlucoseDetails.timeLine.map((entry: any) => {
          const converted = this.convertUtcIsoToMemberLocal(entry.date, timeZone);
          return converted ? { ...entry, date: converted } : entry;
        })
      : this.rawGlucoseDetails.timeLine;

    return { ...this.rawGlucoseDetails, readings, timeLine };
  }

  private convertReadingTimestampsToMemberTimeZone(reading: any, timeZone: string): any {
    const converted: any = {};

    const timestamp = this.convertUtcIsoToMemberLocal(reading.timestamp, timeZone);
    if (timestamp) converted.timestamp = timestamp;

    const timeStampKeyVal = this.convertUtcIsoToMemberLocal(reading.timeStampKeyVal, timeZone);
    if (timeStampKeyVal) converted.timeStampKeyVal = timeStampKeyVal;

    if (typeof reading.timeStampKey === 'number') {
      const m = moment.tz(reading.timeStampKey, timeZone);
      if (m.isValid()) {
        // Re-expressed so the member's local wall-clock digits read correctly even when
        // this epoch is later treated as UTC (matching the converted timestamp fields above).
        converted.timeStampKey = Date.UTC(
          m.year(),
          m.month(),
          m.date(),
          m.hour(),
          m.minute(),
          m.second(),
          m.millisecond()
        );
      }
    }

    return converted;
  }

  // Converts a UTC ISO instant (e.g. "2026-08-31T01:55:00Z") into the member's local
  // wall-clock time, formatted with no UTC offset (e.g. "2026-08-31T07:25:00.000").
  private convertUtcIsoToMemberLocal(isoValue: string | undefined, timeZone: string): string | null {
    if (!isoValue) return null;
    const m = moment.tz(isoValue, timeZone);
    return m.isValid() ? m.format('YYYY-MM-DDTHH:mm:ss.SSS') : null;
  }

  private buildBmiPayload(): any {
    return this.bmiData
      ? {
          bmi: this.bmiData.bmi,
          weight: this.bmiData.weight,
          weight_lb: this.bmiData.weight_lb,
          timestamp: this.bmiData.timestamp,
          isMostRecentPreviousReading: this.bmiData.isMostRecentPreviousReading,
        }
      : null;
  }

  // Full member context for the selected date range - sent as the chatbot's opening handshake
  // and as the Generate Insight (/cgm/copilot/analyze) payload.
  private buildMemberContextPayload(): any {
    return {
      memberId: this.memberId,
      bmi_data: this.buildBmiPayload(),
      // Glucose API response for the currently selected date range, with reading timestamps
      // converted from UTC to the member's own timezone before being sent to the chatbot.
      glucose_data: this.buildGlucoseDataForChat(),
      // healthMetrics data (heartrate/rhr/hrv, etc.) from /healthData/healthMetrics for the
      // same date range, sent through as-is.
      health_metrics_data: this.healthMetricsData,
    };
  }

  // Generate Insight: the first click (or the first after the date range changes) hits the
  // analyze API; once a response is in, the button turns filled and later clicks just reopen
  // the Insights modal with the cached result.
  onGenerateInsight(): void {
    if (this.isGeneratingInsight) return;
    if (this.insightGenerated) {
      $('#glucoseInsightModal').modal('show');
      return;
    }

    this.isGeneratingInsight = true;
    this.dashboard.analyzeGlucoseInsights(this.buildMemberContextPayload()).subscribe((response: any) => {
      this.isGeneratingInsight = false;
      if (response === 1) {
        this.insightText = 'Sorry, insights could not be generated. Please try again!';
        this.insightGenerated = false;
      } else {
        this.insightText = this.extractInsightText(response);
        this.insightGenerated = true;
      }
      $('#glucoseInsightModal').modal('show');
    });
  }

  closeInsightModal(): void {
    $('#glucoseInsightModal').modal('hide');
  }

  // Analyze API response shape isn't fixed yet - accept a plain string or the usual text fields,
  // falling back to pretty-printed JSON so nothing returned is ever hidden.
  private extractInsightText(response: any): string {
    if (response == null) return 'No insights were returned for the selected date range.';
    if (typeof response === 'string') return response;
    const text =
      response.response_message ??
      response.insights ??
      response.insight ??
      response.analysis ??
      response.summary ??
      response.message;
    if (typeof text === 'string') return text;
    if (Array.isArray(text)) return text.map((t: any) => (typeof t === 'string' ? `- ${t}` : `- ${JSON.stringify(t)}`)).join('\n');
    return JSON.stringify(text ?? response, null, 2);
  }

  openChatBot(): void {
    this.showHideChatBot = true;
    // Only kick off a new conversation the first time the chat is opened for this tab
    // instance - reopening the panel (without navigating away) resumes the same one.
    if (!this.chatReady && !this.isSending) {
      this.startConversation();
    } else {
      // Panel was just re-created by *ngIf, so it renders scrolled to the top by default -
      // jump straight to the latest message instead.
      this.scrollToBottom(0);
    }
  }

  hideChatBot(): void {
    // Only collapses the panel - the conversation stays alive as long as the user is on
    // this tab. <app-glucose> is behind *ngIf="selectedMainTab === 'glucose'" in
    // member-dashboard.component.html, so Angular destroys this component (resetting
    // conversationId/messages/chatReady for free) when the user switches to a different
    // tab, and a brand new co-pilot/conversation starts the next time they open the tab.
    this.showHideChatBot = false;
  }

  // First call whenever a new conversation starts: sends member context (BMI + raw glucose
  // data) with no user_message, per the GlycoBrain handshake. The API replies with just a
  // conversation_id (response_message is null) - we show our own fixed welcome message and
  // only then let the user type, since the API has nothing to say back at this point.
  private startConversation(): void {
    this.isSending = true;

    const payload = this.buildMemberContextPayload();
    this.glucoseDataDirty = false;

    this.dashboard.sendGlucoseChatMessage(payload).subscribe((response: any) => {
      this.isSending = false;
      if (response === 1) {
        this.messages.push({
          sender: 'assistant',
          text: 'Sorry, GlycoBrain could not start a conversation. Please try again!',
          id: new Date().getTime(),
        });
        return;
      }
      this.conversationId = response?.conversation_id ?? null;
      this.chatReady = true;
      this.messages.push({
        sender: 'assistant',
        text:
          "👋 **Hi, I'm GlycoBrain.**\n\n" +
          "Your personal glucose copilot. I can help you understand your glucose data, uncover patterns, and make sense of your trends.\n\n" +
          'What can I help you with today?',
        id: new Date().getTime(),
      });
      this.scrollToBottom(100);
    });
  }

  // onResponse runs after a successful GlycoBrain reply (lastResponseMessage already updated).
  sendMessage(onResponse?: () => void): void {
    const trimmedMessage = this.userMessage.trim();
    if (!trimmedMessage || this.isSending || !this.chatReady) return;

    this.isSending = true;

    this.messages.push({
      sender: 'user',
      text: trimmedMessage,
      id: new Date().getTime(),
    });
    this.messages.push({
      sender: 'assistant',
      text: 'Processing...',
      id: new Date().getTime() + 1,
    });
    this.scrollToBottom(100);

    // The conversation's member context (BMI + glucose + health metrics data) was already sent
    // once in the startConversation() handshake, so every chat message is this lean shape -
    // unless the date range was changed since the last message, in which case the refreshed
    // glucose_data/health_metrics_data are re-sent alongside it so the backend stays in sync.
    const payload: any = {
      conversation_id: this.conversationId,
      memberId: this.memberId,
      user_message: trimmedMessage,
      ...(this.glucoseDataDirty
        ? {
            glucose_data: this.buildGlucoseDataForChat(),
            health_metrics_data: this.healthMetricsData,
          }
        : {}),
    };
    this.glucoseDataDirty = false;

    this.dashboard.sendGlucoseChatMessage(payload).subscribe((response: any) => {
      this.messages.pop();
      if (response === 1) {
        this.messages.push({
          sender: 'assistant',
          text: 'Sorry, there was an error while processing your request. Please try again!',
          id: new Date().getTime() + 1,
        });
      } else {
        this.conversationId = response?.conversation_id ?? this.conversationId;
        if (response?.response_message) {
          this.lastResponseMessage = response.response_message;
        }
        this.messages.push({
          sender: 'assistant',
          text: response?.response_message || 'Sorry, I could not process that.',
          id: new Date().getTime() + 1,
        });
      }
      this.isSending = false;
      this.scrollToBottom(200);
      if (response !== 1 && response?.response_message) {
        onResponse?.();
      }
    });

    this.userMessage = '';
  }

  // Generate Message button: puts "summary" in the chat input and sends it to GlycoBrain.
  // If the summary has several insights the user picks which to use; otherwise the Momo
  // message is generated from the whole summary straight away.
  generateSummaryMessage(): void {
    if (this.isSending || this.isGeneratingMessage) return;
    // Start the flow over: clear the previous insight/archetype selection and the Renee message box.
    this.showInsightPicker = false;
    this.summaryInsights = [];
    this.archetypes.forEach((archetype) => (archetype.selected = false));
    this.showArchetypeDropdown = false;
    clearTimeout(this.rallyDeliveredTimer);
    this.rallyDelivered = false;
    this.showRallyMessageBox = false;
    this.rallyMessage = '';
    this.rallySendStatus = null;
    this.userMessage = 'summary';
    this.sendMessage(() => {
      const insights = this.parseInsights(this.lastResponseMessage);
      if (insights.length > 1) {
        this.summaryInsights = insights.map((insight) => ({ ...insight, selected: false }));
        this.showInsightPicker = true;
        this.scrollToBottom(200);
      } else {
        this.generateMessage();
      }
    });
  }

  get selectedArchetypes(): string[] {
    return this.archetypes.filter((archetype) => archetype.selected).map((archetype) => archetype.name);
  }

  get archetypeDropdownLabel(): string {
    return this.selectedArchetypes[0] || 'Select archetype';
  }

  // Only one archetype can be selected - choosing one clears the rest, choosing the selected
  // one again clears it. The dropdown closes once a choice is made.
  toggleArchetype(archetype: { name: string; selected: boolean }): void {
    const select = !archetype.selected;
    this.archetypes.forEach((item) => (item.selected = false));
    archetype.selected = select;
    this.showArchetypeDropdown = false;
  }

  get hasSelectedInsights(): boolean {
    return this.summaryInsights.some((insight) => insight.selected);
  }

  // Generates the Momo message from only the ticked insights.
  generateFromSelectedInsights(): void {
    const selected = this.summaryInsights.filter((insight) => insight.selected);
    if (!selected.length) return;
    this.showInsightPicker = false;
    this.generateMessage(selected.map((insight) => insight.text));
  }

  // Splits a GlycoBrain response into its "Insight N" sections (a "### Insight 1" heading or
  // a "**Insight 1**" line). Each section runs until the next insight, or until a heading at
  // the same or a higher level than the insight heading (e.g. the next "##" section).
  // Each insight also records the "... Response Summary" section it sits under (e.g. "Food
  // Response Summary"), since every section numbers its insights from 1.
  private parseInsights(text: string): { number: string; section: string; text: string }[] {
    const insightRe = /^\s*(#{1,6})?\s*\**\s*Insight\s*#?\s*(\d+)\b/i;
    const sectionRe = /^\s*(?:#{1,6}\s*)?\**\s*([^*#]*Response Summary)\s*\**\s*:?\s*$/i;
    const headingRe = /^\s*(#{1,6})\s+/;
    const insights: { number: string; section: string; text: string }[] = [];
    let section = '';
    let current: { number: string; section: string; lines: string[]; level: number } | null = null;
    const closeCurrent = () => {
      if (current) insights.push({ number: current.number, section: current.section, text: current.lines.join('\n').trim() });
      current = null;
    };

    for (const line of (text || '').split(/\r?\n/)) {
      const sectionMatch = line.match(sectionRe);
      if (sectionMatch) {
        closeCurrent();
        section = sectionMatch[1].trim();
        continue;
      }
      const insightMatch = line.match(insightRe);
      if (insightMatch) {
        closeCurrent();
        // Drop the "Insight N" label itself, keeping any title/text on the same line.
        const rest = line.replace(insightRe, '').replace(/^[\s*:.\-–]+/, '');
        current = { number: insightMatch[2], section, lines: [rest], level: insightMatch[1]?.length ?? 0 };
        continue;
      }
      const headingMatch = line.match(headingRe);
      if (current && current.level && headingMatch && headingMatch[1].length <= current.level) {
        closeCurrent();
        continue;
      }
      if (current) current.lines.push(line);
    }
    closeCurrent();
    return insights;
  }

  // Generate Message (Momo NBA): sends the most recent GlycoBrain response_message with the
  // member, the date range selected in the glucose date picker, the archetypes ticked in the
  // dropdown and the insights ticked in the picker (empty when there was no picker).
  generateMessage(insights: string[] = []): void {
    if (!this.lastResponseMessage || this.isGeneratingMessage || this.isSending) return;

    this.isGeneratingMessage = true;
    this.showArchetypeDropdown = false;
    const payload = {
      member_id: this.memberId,
      response_message: this.lastResponseMessage,
      start_date: this.startDate,
      end_date: this.endDate,
      archetypes: this.selectedArchetypes,
      insights,
    };

    // Generating again while a delivered confirmation is still up - drop it straight away.
    clearTimeout(this.rallyDeliveredTimer);
    this.rallyDelivered = false;
    this.showRallyMessageBox = true;
    this.rallyMessage = '';
    this.rallySendStatus = null;
    this.dashboard.generateNbaMessage(payload).subscribe((response: any) => {
      this.isGeneratingMessage = false;
      // Response or error, whatever comes back goes into the editable Rally message box.
      this.rallyMessage = response?.isError
        ? this.extractMomoError(response.error, 'The message could not be generated.')
        : this.extractInsightText(response);
      this.scrollToBottom(200);
    });
  }

  // Sends the (possibly edited) Rally message box text to the member.
  sendRallyMessage(): void {
    const message = this.rallyMessage.trim();
    if (!message || this.isSendingRallyMessage || this.isGeneratingMessage) return;

    this.isSendingRallyMessage = true;
    this.rallySendStatus = null;
    this.dashboard
      .sendNbaGlycobrainMessage({ member_id: this.memberId, message, action: 'none' })
      .subscribe((response: any) => {
        this.isSendingRallyMessage = false;
        if (response?.isError) {
          // Keep the box open with the error so the message can be retried.
          this.rallySendStatus = { success: false, text: this.extractMomoError(response.error, 'The message could not be sent.') };
          return;
        }
        if (response?.success !== true) {
          this.rallySendStatus = {
            success: false,
            text: (typeof response?.message === 'string' && response.message) || 'The message could not be sent.',
          };
          return;
        }
        // Delivered - swap the text box for a confirmation, then after 20s hide the whole
        // section (back to the initial state with only the Generate Message button).
        this.rallyMessage = '';
        this.rallySendStatus = null;
        this.rallyDelivered = true;
        clearTimeout(this.rallyDeliveredTimer);
        this.rallyDeliveredTimer = setTimeout(() => this.resetRallyMessageBox(), 20000);
      });
  }

  private resetRallyMessageBox(): void {
    clearTimeout(this.rallyDeliveredTimer);
    this.rallyDelivered = false;
    this.showRallyMessageBox = false;
    this.rallyMessage = '';
    this.rallySendStatus = null;
  }

  ngOnDestroy(): void {
    clearTimeout(this.rallyDeliveredTimer);
  }

  // Pulls the most useful text out of an HttpErrorResponse (FastAPI puts it in error.detail).
  private extractMomoError(error: any, fallback: string): string {
    const body = error?.error;
    const detail = body?.detail ?? body?.message ?? body?.error ?? body;
    let text: string;
    if (typeof detail === 'string' && detail.trim()) {
      text = detail;
    } else if (detail && typeof detail === 'object') {
      text = JSON.stringify(detail);
    } else {
      text = error?.message || fallback;
    }
    return error?.status ? `Error ${error.status}: ${text}` : text;
  }

  /** Rich chat bubble: **bold** text, numbered lists, and blank-line paragraph breaks. */
  getChatMessageHtml(text: string | null | undefined): SafeHtml {
    if (text == null || text === '') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.chatMessageToHtml(text));
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private chatInline(text: string): string {
    // "\*" is a markdown escape for a literal asterisk (not an emphasis marker) - protect it
    // behind a placeholder before running the bold/italic regexes below, then restore it as a
    // plain "*" afterwards, so it neither triggers emphasis nor leaks the backslash into the UI.
    const ESCAPED_ASTERISK = '\u0001';
    // The API sometimes embeds a literal "<br>" inside table cells (markdown has no native way
    // to break a line within a cell) - protect it the same way so escapeHtml doesn't turn it
    // into visible "&lt;br&gt;" text, then turn it into a real line break afterwards.
    const BR_TAG = '\u0002';

    let h = text.replace(/\\\*/g, ESCAPED_ASTERISK);
    h = h.replace(/<br\s*\/?>/gi, BR_TAG);
    h = this.escapeHtml(h);
    // Bold first, so a leftover single "*" from **text** pairs is never mistaken for italics.
    h = h.replace(/\*\*([\s\S]*?)\*\*/g, '<strong>$1</strong>');
    h = h.replace(/\*([^*]+?)\*/g, '<em>$1</em>');
    h = h.split(ESCAPED_ASTERISK).join('*');
    h = h.split(BR_TAG).join('<br/>');
    return h;
  }

  // Collects consecutive list items matching itemRe, bridging over blank lines as long as
  // the next non-blank line continues the same list - the API sends each numbered/bulleted
  // point as its own "\n\n"-separated paragraph, and without this a blank line would end the
  // list early, splitting it into several single-item lists that each restart at "1.".
  private collectListItems(
    lines: string[],
    start: number,
    itemRe: RegExp
  ): { items: string[]; next: number } {
    const items: string[] = [];
    let i = start;

    while (i < lines.length) {
      if (lines[i].trim() === '') {
        let j = i;
        while (j < lines.length && lines[j].trim() === '') j++;
        if (j < lines.length && itemRe.test(lines[j])) {
          i = j;
          continue;
        }
        break;
      }

      const m = lines[i].match(itemRe);
      if (!m) break;
      items.push(`<li>${this.chatInline(m[1])}</li>`);
      i++;
    }

    return { items, next: i };
  }

  // Splits a GFM table row ("| a | b |" or "a | b") into trimmed cell strings.
  private splitTableRow(line: string): string[] {
    let trimmed = line.trim();
    if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
    if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
    return trimmed.split('|').map((cell) => cell.trim());
  }

  private isTableRow(line: string): boolean {
    return line.includes('|');
  }

  // A GFM separator row looks like "|---|---:|:---:|" - every cell is dashes with
  // optional leading/trailing colons for alignment.
  private isTableSeparatorRow(line: string): boolean {
    const cells = this.splitTableRow(line);
    return cells.length > 0 && cells.every((cell) => /^:?-{2,}:?$/.test(cell));
  }

  // True only when `lines[idx]` is an actual table header - i.e. the *next* line is a valid
  // separator row. A bare line containing "|" with no separator after it is just text and
  // must NOT be treated as a table (or the paragraph loop below would stall on it forever).
  private isTableStart(lines: string[], idx: number): boolean {
    return (
      this.isTableRow(lines[idx]) &&
      idx + 1 < lines.length &&
      this.isTableSeparatorRow(lines[idx + 1])
    );
  }

  // Small markdown-lite block parser: headings (#..######), bullet (-/*) and numbered (1.)
  // lists, GFM tables, horizontal rules (---/***/___), **bold**/*italic* inline text, and
  // blank-line paragraph breaks - covers the shapes the GlycoBrain API actually sends (e.g.
  // "#### Observation" sections, "- " bullet points, "---" dividers, and "| a | b |" tables).
  private chatMessageToHtml(raw: string): string {
    const lines = raw.replace(/\r\n|\r/g, '\n').split('\n');
    const blocks: string[] = [];
    let i = 0;

    const headingRe = /^(#{1,6})\s+(.*)$/;
    const bulletRe = /^[-*]\s+(.*)$/;
    const numberedRe = /^\d+\.\s+(.*)$/;
    const hrRe = /^(-{3,}|\*{3,}|_{3,})$/;

    while (i < lines.length) {
      const line = lines[i];

      if (line.trim() === '') {
        i++;
        continue;
      }

      if (hrRe.test(line.trim())) {
        blocks.push('<hr class="glucoseChatDivider" />');
        i++;
        continue;
      }

      if (this.isTableStart(lines, i)) {
        const headerCells = this.splitTableRow(line);
        i += 2; // skip header row + separator row

        const bodyRows: string[][] = [];
        while (i < lines.length && this.isTableRow(lines[i])) {
          bodyRows.push(this.splitTableRow(lines[i]));
          i++;
        }

        const thead = `<thead><tr>${headerCells
          .map((cell) => `<th>${this.chatInline(cell)}</th>`)
          .join('')}</tr></thead>`;
        const tbody = `<tbody>${bodyRows
          .map(
            (row) =>
              `<tr>${row.map((cell) => `<td>${this.chatInline(cell)}</td>`).join('')}</tr>`
          )
          .join('')}</tbody>`;
        blocks.push(
          `<div class="glucoseChatTableWrap"><table class="glucoseChatTable">${thead}${tbody}</table></div>`
        );
        continue;
      }

      const headingMatch = line.match(headingRe);
      if (headingMatch) {
        // Level class (h1..h6) lets the Insights modal give "##" sections, "###" insights and
        // "####" sub-labels distinct sizes; the chat bubble ignores it.
        const level = headingMatch[1].length;
        blocks.push(
          `<p class="glucoseChatHeading glucoseChatHeading-h${level}">${this.chatInline(headingMatch[2].trim())}</p>`
        );
        i++;
        continue;
      }

      if (bulletRe.test(line)) {
        const { items, next } = this.collectListItems(lines, i, bulletRe);
        blocks.push(`<ul class="glucoseChatList">${items.join('')}</ul>`);
        i = next;
        continue;
      }

      if (numberedRe.test(line)) {
        const { items, next } = this.collectListItems(lines, i, numberedRe);
        blocks.push(`<ol class="glucoseChatList">${items.join('')}</ol>`);
        i = next;
        continue;
      }

      const paragraphLines: string[] = [this.chatInline(line)];
      i++;
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        !headingRe.test(lines[i]) &&
        !bulletRe.test(lines[i]) &&
        !numberedRe.test(lines[i]) &&
        !hrRe.test(lines[i].trim()) &&
        !this.isTableStart(lines, i)
      ) {
        paragraphLines.push(this.chatInline(lines[i]));
        i++;
      }
      blocks.push(`<p class="glucoseChatParagraph">${paragraphLines.join('<br/>')}</p>`);
    }

    return blocks.join('');
  }

  private scrollToBottom(timeout: number): void {
    setTimeout(() => {
      const last = this.messages[this.messages.length - 1];
      document.getElementById(last?.id + '')?.scrollIntoView();
    }, timeout);
  }
}
